import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import { ChertoExperiments, Experiments } from 'constants/experiments'
import { parseStateEnabledCountry } from 'constants/recommendations'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectTopDealsHeroOfferListFilters } from 'selectors/recommendations'
import { getHeroRecommendationEligibility, MINIMUM_HERO_RECOMMENDATIONS } from './heroRecommendationEligibility'

interface Options {
  currentRegionCode: string
  lereHeroCarouselEnabled: boolean
  shouldFetchTwoTowersForHero: boolean
  isPostLoginExperimentSyncing: boolean
  hasCampaignTakeover: boolean
  interleavedTwoTowersOfferList: App.OfferList
  heroOffers: App.OfferList
}

export default function useLereHeroRecommendations({
  currentRegionCode,
  lereHeroCarouselEnabled,
  shouldFetchTwoTowersForHero,
  isPostLoginExperimentSyncing,
  hasCampaignTakeover,
  interleavedTwoTowersOfferList,
  heroOffers,
}: Options) {
  const hasEnteredStateBasedExperiment = useAppSelector(state =>
    state.cherto.chertoExperiments[ChertoExperiments.stateBasedHomePageHero.id]?.triggered === true,
  )
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const canFetchStateBasedHero = config.LERE_ENABLED && lereHeroCarouselEnabled && config.BRAND === LUXURY_ESCAPES &&
    currentRegionCode === parseStateEnabledCountry && !isPostLoginExperimentSyncing && !hasCampaignTakeover
  const canFetchTopDealsHeroCarousel =
    lereHeroCarouselEnabled &&
    config.BRAND === LUXURY_ESCAPES &&
    !isPostLoginExperimentSyncing &&
    (currentRegionCode !== parseStateEnabledCountry || canFetchStateBasedHero) &&
    !hasCampaignTakeover
  const topDealsHeroOfferListFilters = useAppSelector(selectTopDealsHeroOfferListFilters)
  const topDealsHeroOfferList = useOfferList(topDealsHeroOfferListFilters, { disabled: !canFetchTopDealsHeroCarousel, preferListingOffers: true })
  const { personalisedEligible, stateBasedEligible } = getHeroRecommendationEligibility(
    interleavedTwoTowersOfferList, topDealsHeroOfferList,
  )
  const canActivateStateBasedHero = canFetchStateBasedHero && stateBasedEligible
  const { isAnyVariant: isStateBasedHeroVariant } = useExperiment(ChertoExperiments.stateBasedHomePageHero, {
    canActivate: canActivateStateBasedHero,
  })
  const stateBasedTopDealsReady = isStateBasedHeroVariant && canActivateStateBasedHero

  const canActivateLerePersonalisedHomePageHeroCarousel = shouldFetchTwoTowersForHero &&
    !isSpoofed &&
    !isPostLoginExperimentSyncing &&
    personalisedEligible
  const { variation: lerePersonalisedHomePageHeroCarouselVariant } = useExperiment(
    Experiments.lerePersonalisedHomePageHeroCarouselV7,
    { canActivate: canActivateLerePersonalisedHomePageHeroCarousel },
  )

  // Determine carousel configuration based on experiment variant or fallback logic
  const twoTowersAUHeroCarouselEnabled = canActivateLerePersonalisedHomePageHeroCarousel && lerePersonalisedHomePageHeroCarouselVariant === 'on'

  // Assignment alone is not cohort entry; retain lower placement only after activation.
  const keepTopDealsBelowHero = isStateBasedHeroVariant && hasEnteredStateBasedExperiment &&
    twoTowersAUHeroCarouselEnabled && !topDealsHeroOfferList.fetching && !topDealsHeroOfferList.error &&
    topDealsHeroOfferList.offerIds.length >= MINIMUM_HERO_RECOMMENDATIONS

  const isTopDealsHeroLoading = canFetchTopDealsHeroCarousel &&
    (currentRegionCode !== parseStateEnabledCountry || isStateBasedHeroVariant) && topDealsHeroOfferList.fetching
  const topDealsCarouselEnabled = canFetchTopDealsHeroCarousel &&
    (currentRegionCode !== parseStateEnabledCountry || stateBasedTopDealsReady || keepTopDealsBelowHero) &&
    !isTopDealsHeroLoading && topDealsHeroOfferList.offerIds.length > 0

  return {
    isTopDealsHeroLoading,
    topDealsHeroOfferList,
    curatedOfferList: topDealsCarouselEnabled ? topDealsHeroOfferList : heroOffers,
    stateBasedTopDealsReady,
    topDealsCarouselEnabled,
    twoTowersAUHeroCarouselEnabled,
  }
}
